/**
 * AssistantDockContext — omnipresent open/close state for the AssistantDock.
 * Shared between FmTopBar (which triggers open via 'Ask' job) and the dock
 * itself. Mounted at AppLayout level so it spans all protected routes.
 */
import React, { createContext, useContext, useCallback, useState, type ReactNode } from 'react';
import type { AssistantContentDescriptor, PlanAssistantAsk } from '@/contexts/planAssistant.types';
import { assistantContentForContext } from '@/shared/ui/planAssistantContextGuard';

export interface AssistantContextSeedDescriptor<TContext extends object = object> {
  title: string;
  summary: string;
  /** Optional route-specific copy for the persistent launcher. */
  triggerLabel?: string;
  context: Readonly<TContext>;
}

export interface AssistantContextSeedInput<
  TContext extends object = object,
> extends AssistantContextSeedDescriptor<TContext> {
  planAssistantAsk?: PlanAssistantAsk;
  onContextExpired?: () => void;
}

type AssistantDockState =
  | { status: 'closed' }
  | { status: 'open'; content: { kind: 'default' } }
  | { status: 'open'; content: { kind: 'node'; body: ReactNode } }
  | {
      status: 'open';
      content: {
        kind: 'context';
        descriptor: AssistantContextSeedDescriptor;
        assistantContent: AssistantContentDescriptor;
      };
    };

export type AssistantDockContent = Exclude<AssistantDockState, { status: 'closed' }>['content'];

interface AssistantDockContextType {
  isOpen: boolean;
  /** The explicit content contract rendered by the shared dock. */
  content: AssistantDockContent | null;
  /**
   * Compatibility view of route-supplied node content. New renderers should
   * consume the discriminated content contract instead.
   */
  seed: ReactNode | null;
  /** Compatibility view used by routes to detect their currently open context. */
  contextSeed: AssistantContextSeedDescriptor | null;
  /** Route-specific launcher copy while contextual help is registered. */
  launcherLabel: string | null;
  /** Open the dock with the generic (unseeded) body. */
  open: () => void;
  /** Open the dock pre-seeded with route context. */
  openWith: (seed: ReactNode) => void;
  /** Open the dock with route context adapted to a shared content descriptor. */
  openWithContext: <TContext extends object>(seed: AssistantContextSeedInput<TContext>) => void;
  /** Register route context for the omnipresent launcher without opening it. */
  registerContext: <TContext extends object>(seed: AssistantContextSeedInput<TContext>) => void;
  /** Clear a registration only when it still belongs to this route instance. */
  clearRegisteredContext: (context: object) => void;
  close: () => void;
  toggle: () => void;
}

const AssistantDockContext = createContext<AssistantDockContextType | undefined>(undefined);

export const AssistantDockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dock, setDock] = useState<AssistantDockState>({ status: 'closed' });
  const [registeredContext, setRegisteredContext] = useState<Extract<
    AssistantDockContent,
    { kind: 'context' }
  > | null>(null);
  const isOpen = dock.status === 'open';
  const content = dock.status === 'open' ? dock.content : null;
  const seed = content?.kind === 'node' ? content.body : null;
  const contextSeed = content?.kind === 'context' ? content.descriptor : null;
  const launcherLabel = registeredContext?.descriptor.triggerLabel ?? null;

  const open = useCallback(() => {
    if (registeredContext) {
      setDock({ status: 'open', content: registeredContext });
      return;
    }
    setDock({ status: 'open', content: { kind: 'default' } });
  }, [registeredContext]);
  const openWith = useCallback((next: ReactNode) => {
    setDock({ status: 'open', content: { kind: 'node', body: next } });
  }, []);
  const openWithContext = useCallback(
    <TContext extends object>(next: AssistantContextSeedInput<TContext>) => {
      const {
        title,
        summary,
        triggerLabel,
        context: routeContext,
        planAssistantAsk,
        onContextExpired,
      } = next;
      setDock({
        status: 'open',
        content: {
          kind: 'context',
          descriptor: { title, summary, triggerLabel, context: routeContext },
          assistantContent: assistantContentForContext(
            routeContext,
            planAssistantAsk,
            onContextExpired,
          ),
        },
      });
    },
    [],
  );
  const registerContext = useCallback(
    <TContext extends object>(next: AssistantContextSeedInput<TContext>) => {
      const {
        title,
        summary,
        triggerLabel,
        context: routeContext,
        planAssistantAsk,
        onContextExpired,
      } = next;
      setRegisteredContext({
        kind: 'context',
        descriptor: { title, summary, triggerLabel, context: routeContext },
        assistantContent: assistantContentForContext(
          routeContext,
          planAssistantAsk,
          onContextExpired,
        ),
      });
    },
    [],
  );
  const clearRegisteredContext = useCallback((routeContext: object) => {
    setRegisteredContext((current) =>
      current?.descriptor.context === routeContext ? null : current,
    );
  }, []);
  // Closing clears the seed so a later generic open() does not resurface stale
  // route context.
  const close = useCallback(() => setDock({ status: 'closed' }), []);
  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [close, isOpen, open]);

  return (
    <AssistantDockContext.Provider
      value={{
        isOpen,
        content,
        seed,
        contextSeed,
        launcherLabel,
        open,
        openWith,
        openWithContext,
        registerContext,
        clearRegisteredContext,
        close,
        toggle,
      }}
    >
      {children}
    </AssistantDockContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- standard context hook; same-file export is the project pattern (AuthContext, MapContext)
export const useAssistantDock = (): AssistantDockContextType => {
  const ctx = useContext(AssistantDockContext);
  if (!ctx) throw new Error('useAssistantDock must be used within AssistantDockProvider');
  return ctx;
};
